/**
 * VideoAgent - Storyboard-based video editing request tool
 * Users add AI-generated video clips, describe shot requirements,
 * and submit to human editors for professional editing
 */

import React, { useState, useRef, useCallback } from 'react';
import {
    Plus, Trash2, GripVertical,
    Film, Loader2, Download, Clock,
    ChevronDown, AlertCircle, Crown, ArrowRight,
    Sparkles, Video, Send, X, Check, Upload, Eye,
    RotateCcw, FileVideo, Link2, Clipboard, CheckCircle,
    Edit3, MessageSquare, ListOrdered, ExternalLink
} from 'lucide-react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * Create a default shot for the storyboard
 */
const createDefaultShot = (index) => ({
    id: `shot-${Date.now()}-${index}`,
    videoUrl: '',
    videoName: '',
    thumbnailUrl: '',
    duration: '',
    instructions: '',
    order: index
});

/**
 * Shot Card Component - Individual storyboard shot with video
 */
const ShotCard = ({
    shot,
    index,
    onUpdate,
    onDelete,
    onSelectFromLibrary,
    onVideoUpload,
    canDelete,
    isSubmitting
}) => {
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const handleVideoClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            onVideoUpload(shot.id, file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('video/')) {
            onVideoUpload(shot.id, file);
        }
    };

    return (
        <Reorder.Item
            value={shot}
            id={shot.id}
            className="group"
            dragListener={!isSubmitting}
        >
            <motion.div
                layout
                className={`relative flex gap-4 p-4 bg-white/50 dark:bg-slate-800/50 rounded-xl border-2 transition-all ${
                    isDragOver
                        ? 'border-violet-500 bg-violet-50/50 dark:bg-violet-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-600'
                }`}
            >
                {/* Drag Handle */}
                <div className="flex items-center text-slate-400 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical className="w-5 h-5" />
                </div>

                {/* Shot Number Badge */}
                <div className="absolute -top-3 left-4 px-2 py-0.5 bg-violet-500 text-white text-xs font-bold rounded-full">
                    Shot {index + 1}
                </div>

                {/* Video Preview Area */}
                <div
                    onClick={() => !shot.videoUrl && !isSubmitting && onSelectFromLibrary(shot.id)}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative w-40 h-24 flex-shrink-0 rounded-lg border-2 border-dashed transition-all overflow-hidden ${
                        isDragOver
                            ? 'border-violet-500 bg-violet-100 dark:bg-violet-900/30'
                            : shot.videoUrl
                            ? 'border-transparent bg-slate-900'
                            : 'border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700/50 cursor-pointer hover:border-violet-400 dark:hover:border-violet-500'
                    }`}
                >
                    {shot.videoUrl ? (
                        <>
                            {shot.thumbnailUrl ? (
                                <img
                                    src={shot.thumbnailUrl}
                                    alt={`Shot ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <video
                                    src={shot.videoUrl}
                                    className="w-full h-full object-cover"
                                    muted
                                />
                            )}
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                <Video className="w-8 h-8 text-white/80" />
                            </div>
                            <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] text-white font-medium">
                                {shot.duration || 'Video'}
                            </div>
                            {/* Remove video button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onUpdate(shot.id, { videoUrl: '', videoName: '', thumbnailUrl: '', duration: '' });
                                }}
                                className="absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-slate-400 hover:text-violet-500 p-2 transition-colors">
                            <FileVideo className="w-6 h-6 mb-1" />
                            <span className="text-[10px] text-center">Add video clip</span>
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                </div>

                {/* Shot Content */}
                <div className="flex-1 min-w-0 space-y-3">
                    {/* Video Source Actions */}
                    {!shot.videoUrl && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => onSelectFromLibrary(shot.id)}
                                disabled={isSubmitting}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors disabled:opacity-50"
                            >
                                <Film className="w-3.5 h-3.5" />
                                Select from My Videos
                            </button>
                            <button
                                onClick={handleVideoClick}
                                disabled={isSubmitting}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                            >
                                <Upload className="w-3.5 h-3.5" />
                                Upload Video
                            </button>
                        </div>
                    )}

                    {/* Video Name */}
                    {shot.videoUrl && (
                        <div className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                            <span className="text-slate-700 dark:text-slate-300 truncate">
                                {shot.videoName || 'Video added'}
                            </span>
                            <a
                                href={shot.videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-violet-500 hover:text-violet-600"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </div>
                    )}

                    {/* Shot Instructions */}
                    <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                            Shot Instructions
                        </label>
                        <textarea
                            value={shot.instructions}
                            onChange={(e) => onUpdate(shot.id, { instructions: e.target.value })}
                            placeholder="Describe what you want for this shot (e.g., 'Slow zoom in, add text overlay: Open Floor Plan', 'Speed up 2x', 'Add transition to next clip')"
                            disabled={isSubmitting}
                            rows={2}
                            className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent placeholder-slate-400 dark:placeholder-slate-500 text-slate-700 dark:text-slate-200"
                        />
                    </div>
                </div>

                {/* Delete Button */}
                {canDelete && (
                    <button
                        onClick={() => onDelete(shot.id)}
                        disabled={isSubmitting}
                        className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </motion.div>
        </Reorder.Item>
    );
};

ShotCard.propTypes = {
    shot: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSelectFromLibrary: PropTypes.func.isRequired,
    onVideoUpload: PropTypes.func.isRequired,
    canDelete: PropTypes.bool,
    isSubmitting: PropTypes.bool
};

/**
 * Add Shot Button
 */
const AddShotButton = ({ onClick, disabled }) => (
    <div className="flex justify-center py-2">
        <button
            onClick={onClick}
            disabled={disabled}
            className="flex items-center gap-1 px-3 py-1.5 text-xs text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-full border border-dashed border-violet-300 dark:border-violet-600 transition-all hover:border-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <Plus className="w-3 h-3" />
            <span>Add shot</span>
        </button>
    </div>
);

AddShotButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

/**
 * Video Library Modal - Select from My Videos
 */
const VideoLibraryModal = ({ isOpen, onClose, onSelect, videos, isLoading }) => {
    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800 dark:text-white">Select Video from Library</h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto max-h-[60vh]">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
                        </div>
                    ) : videos.length === 0 ? (
                        <div className="text-center py-12">
                            <Video className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                            <p className="text-slate-500 dark:text-slate-400">No videos in your library yet</p>
                            <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                                Generate videos in Virtual Staging first
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {videos.map((video) => (
                                <button
                                    key={video.id}
                                    onClick={() => onSelect(video)}
                                    className="group relative rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-violet-500 dark:hover:border-violet-500 overflow-hidden transition-all text-left"
                                >
                                    <div className="aspect-video bg-slate-100 dark:bg-slate-900 relative">
                                        {video.thumbnailUrl || video.thumbnail_url ? (
                                            <img
                                                src={video.thumbnailUrl || video.thumbnail_url}
                                                alt={video.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Video className="w-10 h-10 text-slate-300 dark:text-slate-600" />
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-violet-500/0 group-hover:bg-violet-500/20 flex items-center justify-center transition-colors">
                                            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all">
                                                <Check className="w-6 h-6 text-violet-600" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <p className="font-medium text-slate-800 dark:text-white text-sm truncate">
                                            {video.name || video.title || 'Untitled Video'}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            {video.duration || 'Video clip'}
                                        </p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

VideoLibraryModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    videos: PropTypes.array.isRequired,
    isLoading: PropTypes.bool
};

/**
 * Success Modal - After submission
 */
const SuccessModal = ({ isOpen, onClose, jobId }) => {
    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
                    Request Submitted!
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                    Your video editing request has been sent to our professional editors.
                    You'll receive the final video within 24-48 hours.
                </p>
                {jobId && (
                    <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">
                        Reference: <span className="font-mono text-violet-600 dark:text-violet-400">{jobId}</span>
                    </p>
                )}
                <button
                    onClick={onClose}
                    className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium transition-colors"
                >
                    Done
                </button>
            </motion.div>
        </motion.div>
    );
};

SuccessModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    jobId: PropTypes.string
};

/**
 * Upgrade Prompt Component
 */
const UpgradePrompt = ({ onUpgrade }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-600 p-8 text-white"
    >
        <div className="absolute inset-0 overflow-hidden">
            <motion.div
                animate={{ x: [0, 100, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"
            />
        </div>

        <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                    <Crown className="w-8 h-8" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold">Professional Video Editing</h2>
                    <p className="text-white/80">Available on Ultra plan</p>
                </div>
            </div>

            <p className="text-lg text-white/90 mb-6 max-w-lg">
                Create stunning marketing videos with our professional editing team.
                Submit your AI-generated clips with instructions and receive polished, ready-to-use videos.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                    { icon: Film, label: 'Professional Editing' },
                    { icon: Edit3, label: 'Custom Instructions' },
                    { icon: ListOrdered, label: 'Storyboard Control' },
                    { icon: Download, label: 'HD Downloads' }
                ].map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-2 text-white/90">
                        <div className="p-1.5 bg-white/20 rounded-lg">
                            <Icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm">{label}</span>
                    </div>
                ))}
            </div>

            <button
                onClick={onUpgrade}
                className="flex items-center gap-2 px-6 py-3 bg-white text-violet-600 rounded-xl font-semibold hover:bg-white/90 transition-colors shadow-lg"
            >
                <span>Upgrade to Ultra</span>
                <ArrowRight className="w-5 h-5" />
            </button>
        </div>
    </motion.div>
);

UpgradePrompt.propTypes = {
    onUpgrade: PropTypes.func.isRequired
};

/**
 * Main VideoAgent Component
 */
const VideoAgent = ({ subscription, onUpgrade, myVideos = [], onLoadVideos, isLoadingVideos = false }) => {
    // Access check - for now allow all, can be restricted later
    const hasAccess = subscription?.tier === 'ultra' || subscription?.tier === 'rp_ultra' || subscription?.tier === 'enterprise';

    // State
    const [shots, setShots] = useState([createDefaultShot(0), createDefaultShot(1)]);
    const [projectName, setProjectName] = useState('');
    const [overallInstructions, setOverallInstructions] = useState('');
    const [targetDuration, setTargetDuration] = useState('30');
    const [outputFormat, setOutputFormat] = useState('16:9');
    const [musicPreference, setMusicPreference] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [submittedJobId, setSubmittedJobId] = useState(null);

    // Video library modal
    const [showVideoLibrary, setShowVideoLibrary] = useState(false);
    const [selectingForShotId, setSelectingForShotId] = useState(null);

    // Shot management
    const handleUpdateShot = useCallback((shotId, updates) => {
        setShots(prev => prev.map(shot =>
            shot.id === shotId ? { ...shot, ...updates } : shot
        ));
    }, []);

    const handleDeleteShot = useCallback((shotId) => {
        setShots(prev => prev.filter(shot => shot.id !== shotId));
    }, []);

    const handleAddShot = useCallback((afterIndex = shots.length - 1) => {
        const newShot = createDefaultShot(Date.now());
        setShots(prev => {
            const newShots = [...prev];
            newShots.splice(afterIndex + 1, 0, newShot);
            return newShots;
        });
    }, [shots.length]);

    const handleReorderShots = useCallback((newOrder) => {
        setShots(newOrder);
    }, []);

    // Open video library for a specific shot
    const handleSelectFromLibrary = useCallback((shotId) => {
        setSelectingForShotId(shotId);
        setShowVideoLibrary(true);
        if (onLoadVideos) onLoadVideos();
    }, [onLoadVideos]);

    // Handle video selection from library
    const handleVideoSelected = useCallback((video) => {
        if (selectingForShotId) {
            handleUpdateShot(selectingForShotId, {
                videoUrl: video.videoUrl || video.video_url || video.resultUrl || video.result_url,
                videoName: video.name || video.title || 'Video clip',
                thumbnailUrl: video.thumbnailUrl || video.thumbnail_url,
                duration: video.duration || ''
            });
        }
        setShowVideoLibrary(false);
        setSelectingForShotId(null);
    }, [selectingForShotId, handleUpdateShot]);

    // Handle video upload
    const handleVideoUpload = useCallback(async (shotId, file) => {
        // Create local preview
        const localUrl = URL.createObjectURL(file);
        handleUpdateShot(shotId, {
            videoUrl: localUrl,
            videoName: file.name,
            thumbnailUrl: '',
            duration: ''
        });

        // TODO: Upload to server and update with real URL
        // For now, we'll use the local URL
    }, [handleUpdateShot]);

    // Submit to editors
    const handleSubmit = async () => {
        // Validate
        const shotsWithVideo = shots.filter(s => s.videoUrl);
        if (shotsWithVideo.length === 0) {
            setError('Please add at least one video clip to your storyboard.');
            return;
        }

        if (!overallInstructions.trim()) {
            setError('Please provide overall instructions for the final video.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            // Prepare submission data
            const submissionData = {
                projectName: projectName || `Video Project ${new Date().toLocaleDateString()}`,
                shots: shots.map((shot, index) => ({
                    order: index + 1,
                    videoUrl: shot.videoUrl,
                    videoName: shot.videoName,
                    instructions: shot.instructions,
                    duration: shot.duration
                })),
                overallInstructions,
                targetDuration: `${targetDuration} seconds`,
                outputFormat,
                musicPreference,
                submittedAt: new Date().toISOString()
            };

            // TODO: Send to backend API
            // const response = await submitVideoEditRequest(submissionData);
            console.log('Submitting video edit request:', submissionData);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Generate a mock job ID
            const jobId = `VE-${Date.now().toString(36).toUpperCase()}`;
            setSubmittedJobId(jobId);
            setShowSuccess(true);

            // Reset form
            setShots([createDefaultShot(0), createDefaultShot(1)]);
            setProjectName('');
            setOverallInstructions('');
            setMusicPreference('');

        } catch (err) {
            console.error('Failed to submit:', err);
            setError(err.message || 'Failed to submit request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset storyboard
    const handleReset = () => {
        setShots([createDefaultShot(0), createDefaultShot(1)]);
        setProjectName('');
        setOverallInstructions('');
        setMusicPreference('');
        setError(null);
    };

    // Show upgrade prompt if no access
    if (!hasAccess) {
        return (
            <div className="h-full flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 to-violet-50 dark:from-slate-900 dark:to-violet-950">
                <div className="max-w-xl w-full">
                    <UpgradePrompt onUpgrade={onUpgrade} />
                </div>
            </div>
        );
    }

    const shotsWithVideo = shots.filter(s => s.videoUrl).length;

    return (
        <div className="flex-1 h-full min-h-0 flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-violet-50/30 to-fuchsia-50/30 dark:from-slate-900 dark:via-violet-950/30 dark:to-fuchsia-950/30">
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200/50 dark:border-slate-700/50 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/25">
                            <Film className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-800 dark:text-white">Video Assistant</h1>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Create storyboard for professional video editing
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={handleReset}
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <RotateCcw className="w-4 h-4" />
                        Reset
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-h-0 flex overflow-hidden">
                {/* Left Panel - Settings */}
                <div className="w-80 h-full min-h-0 flex-shrink-0 border-r border-slate-200/50 dark:border-slate-700/50 bg-white/30 dark:bg-slate-800/30 backdrop-blur-sm overflow-y-auto">
                    <div className="p-4 space-y-5 pb-6">
                        <div>
                            <h3 className="font-semibold text-slate-700 dark:text-slate-200 mb-4 flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                Project Details
                            </h3>

                            {/* Project Name */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                    Project Name
                                </label>
                                <input
                                    type="text"
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                    placeholder="e.g., 123 Main St Tour"
                                    disabled={isSubmitting}
                                    className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                />
                            </div>

                            {/* Target Duration */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                    Target Duration
                                </label>
                                <select
                                    value={targetDuration}
                                    onChange={(e) => setTargetDuration(e.target.value)}
                                    disabled={isSubmitting}
                                    className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                >
                                    <option value="15">15 seconds</option>
                                    <option value="30">30 seconds</option>
                                    <option value="45">45 seconds</option>
                                    <option value="60">60 seconds (1 min)</option>
                                    <option value="90">90 seconds</option>
                                    <option value="120">120 seconds (2 min)</option>
                                </select>
                            </div>

                            {/* Output Format */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                    Output Format
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { value: '16:9', label: '16:9', desc: 'Landscape' },
                                        { value: '9:16', label: '9:16', desc: 'Portrait' },
                                        { value: '1:1', label: '1:1', desc: 'Square' }
                                    ].map((format) => (
                                        <button
                                            key={format.value}
                                            onClick={() => setOutputFormat(format.value)}
                                            disabled={isSubmitting}
                                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                                outputFormat === format.value
                                                    ? 'bg-violet-100 dark:bg-violet-900/40 border-2 border-violet-500 text-violet-700 dark:text-violet-300'
                                                    : 'bg-white dark:bg-slate-700 border-2 border-transparent text-slate-600 dark:text-slate-300 hover:border-slate-300'
                                            }`}
                                        >
                                            <div>{format.label}</div>
                                            <div className="text-[10px] opacity-60">{format.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Music Preference */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                    Music Preference (optional)
                                </label>
                                <input
                                    type="text"
                                    value={musicPreference}
                                    onChange={(e) => setMusicPreference(e.target.value)}
                                    placeholder="e.g., Upbeat, Modern, Cinematic"
                                    disabled={isSubmitting}
                                    className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Overall Instructions */}
                        <div>
                            <h3 className="font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                                <Edit3 className="w-4 h-4" />
                                Overall Instructions *
                            </h3>
                            <textarea
                                value={overallInstructions}
                                onChange={(e) => setOverallInstructions(e.target.value)}
                                placeholder="Describe what you want for the final video:&#10;&#10;- Overall style and feel&#10;- Text overlays or captions&#10;- Transitions between clips&#10;- Opening/closing titles&#10;- Any specific effects&#10;- Brand colors or logo placement"
                                disabled={isSubmitting}
                                rows={8}
                                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent placeholder-slate-400 dark:placeholder-slate-500"
                            />
                        </div>

                        {/* Summary */}
                        <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl">
                            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Summary</h4>
                            <div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                                <div className="flex justify-between">
                                    <span>Video clips:</span>
                                    <span className="font-medium">{shotsWithVideo} / {shots.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Target duration:</span>
                                    <span className="font-medium">{targetDuration}s</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Format:</span>
                                    <span className="font-medium">{outputFormat}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Storyboard */}
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    {/* Storyboard Header */}
                    <div className="flex-shrink-0 px-6 py-3 border-b border-slate-200/50 dark:border-slate-700/50 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <h2 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                <ListOrdered className="w-4 h-4" />
                                Storyboard
                            </h2>
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                {shots.length} shots • {shotsWithVideo} with video
                            </span>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="mx-6 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
                            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Storyboard Content */}
                    <div className="flex-1 min-h-0 overflow-y-auto p-6">
                        <div className="space-y-2">
                            <Reorder.Group
                                axis="y"
                                values={shots}
                                onReorder={handleReorderShots}
                                className="space-y-2"
                            >
                                {shots.map((shot, index) => (
                                    <React.Fragment key={shot.id}>
                                        <ShotCard
                                            shot={shot}
                                            index={index}
                                            onUpdate={handleUpdateShot}
                                            onDelete={handleDeleteShot}
                                            onSelectFromLibrary={handleSelectFromLibrary}
                                            onVideoUpload={handleVideoUpload}
                                            canDelete={shots.length > 1}
                                            isSubmitting={isSubmitting}
                                        />
                                        {index < shots.length - 1 && (
                                            <AddShotButton
                                                onClick={() => handleAddShot(index)}
                                                disabled={isSubmitting}
                                            />
                                        )}
                                    </React.Fragment>
                                ))}
                            </Reorder.Group>

                            {/* Add Shot at End */}
                            <AddShotButton
                                onClick={() => handleAddShot(shots.length - 1)}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex-shrink-0 px-6 py-4 border-t border-slate-200/50 dark:border-slate-700/50 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                                {shotsWithVideo === 0 ? (
                                    <span className="text-amber-600 dark:text-amber-400">Add video clips to continue</span>
                                ) : (
                                    <span>{shotsWithVideo} clip{shotsWithVideo !== 1 ? 's' : ''} ready for editing</span>
                                )}
                            </div>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting || shotsWithVideo === 0}
                                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white rounded-xl font-semibold shadow-lg shadow-violet-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5" />
                                        <span>Submit to Editor</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Video Library Modal */}
            <AnimatePresence>
                <VideoLibraryModal
                    isOpen={showVideoLibrary}
                    onClose={() => {
                        setShowVideoLibrary(false);
                        setSelectingForShotId(null);
                    }}
                    onSelect={handleVideoSelected}
                    videos={myVideos}
                    isLoading={isLoadingVideos}
                />
            </AnimatePresence>

            {/* Success Modal */}
            <AnimatePresence>
                <SuccessModal
                    isOpen={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    jobId={submittedJobId}
                />
            </AnimatePresence>
        </div>
    );
};

VideoAgent.propTypes = {
    subscription: PropTypes.object,
    onUpgrade: PropTypes.func,
    myVideos: PropTypes.array,
    onLoadVideos: PropTypes.func,
    isLoadingVideos: PropTypes.bool
};

export default VideoAgent;
